import React, { useEffect, useState, useRef } from 'react';
import { Lock, Send } from 'lucide-react';
// --- TYPE DEFINITIONS ---
type Message = {
  id: string;
  sender: 'system' | 'user';
  content: string;
};
type Question = {
  id: string;
  text: string;
  options: string[];
  nextQuestion?: string;
  allowCustom?: boolean; // New flag to control custom input
};
type ConversationState = {
  messages: Message[];
  currentQuestionId: string | null;
  answers: Record<string, string>;
  isTyping: boolean;
  isFinished: boolean;
};
// --- COMPONENT ---
export const PersonalizationChat: React.FC = () => {
  // --- DATA & STATE ---
  // A simple dictionary of all possible questions in the flow.
  const questions: Record<string, Question> = {
    nba: {
      id: 'nba',
      text: "Let's start: how into the NBA are you?",
      options: ["It's my life", "Oh, I'm serious", 'I like to catch a game occasionally', 'I like to watch games with friends', "I'm down for whatever's fun"],
      nextQuestion: 'teams',
      allowCustom: false
    },
    teams: {
      id: 'teams',
      text: 'Do you have a favorite team?',
      options: ['Lakers', 'Warriors', 'Celtics', 'Heat', 'I just like good basketball'],
      nextQuestion: 'attendance',
      allowCustom: true // Allow users to type in a team not listed
    },
    attendance: {
      id: 'attendance',
      text: 'How often do you attend games in person?',
      options: ['Season ticket holder!', 'A few times a season', 'Once or twice a year', "Rarely, but I'd like to more", 'I prefer watching at home/bars'],
      allowCustom: false
    }
  };
  // The main state for the conversation flow.
  const [state, setState] = useState<ConversationState>({
    messages: [{
      id: 'intro',
      sender: 'system',
      content: "Let's do some quick questions to help dial it in, okay? And remember, this is private, we aren't sharing this with anybody rn, okay?"
    }],
    currentQuestionId: null,
    answers: {},
    isTyping: false,
    isFinished: false
  });
  // State for the custom text input field.
  const [customInput, setCustomInput] = useState<string>('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // --- EFFECTS ---
  // This effect kicks off the conversation on the first render.
  useEffect(() => {
    // Show typing indicator, then ask the first question.
    const startConversation = () => {
      setState(s => ({
        ...s,
        isTyping: true
      }));
      setTimeout(() => {
        const firstQuestion = questions.nba;
        setState(s => ({
          ...s,
          messages: [...s.messages, {
            id: firstQuestion.id,
            sender: 'system',
            content: firstQuestion.text
          }],
          currentQuestionId: firstQuestion.id,
          isTyping: false
        }));
      }, 1500);
    };
    startConversation();
  }, []); // Empty dependency array ensures this runs only once.
  // This effect scrolls the chat to the bottom whenever new messages are added.
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [state.messages, state.isTyping]);
  // --- HANDLERS ---
  /**
   * Handles the core logic for processing a user's response,
   * whether from a button or custom input.
   * @param {string} responseText - The text of the user's answer.
   */
  const handleResponse = (responseText: string) => {
    if (!state.currentQuestionId) return;
    const currentQuestion = questions[state.currentQuestionId];
    // Update state with the user's answer.
    setState(s => ({
      ...s,
      messages: [...s.messages, {
        id: `${currentQuestion.id}-answer`,
        sender: 'user',
        content: responseText
      }],
      answers: {
        ...s.answers,
        [currentQuestion.id]: responseText
      },
      currentQuestionId: null,
      isTyping: true
    }));
    // After a delay, ask the next question or end the conversation.
    setTimeout(() => {
      const nextQuestionId = currentQuestion.nextQuestion;
      if (nextQuestionId && questions[nextQuestionId]) {
        const nextQuestion = questions[nextQuestionId];
        setState(s => ({
          ...s,
          messages: [...s.messages, {
            id: nextQuestion.id,
            sender: 'system',
            content: nextQuestion.text
          }],
          currentQuestionId: nextQuestion.id,
          isTyping: false
        }));
      } else {
        // End of the conversation flow.
        setState(s => ({
          ...s,
          messages: [...s.messages, {
            id: 'end',
            sender: 'system',
            content: "Thanks for sharing! I'll use this to personalize your experience. 🎉"
          }],
          isTyping: false,
          isFinished: true
        }));
      }
    }, 1500);
  };
  /**
   * Handles the form submission for custom text input.
   */
  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customInput.trim() === '') return;
    handleResponse(customInput);
    setCustomInput(''); // Clear the input field.
  };
  // --- RENDER ---
  const currentQuestion = state.currentQuestionId ? questions[state.currentQuestionId] : null;
  return <div className="flex items-center justify-center min-h-screen bg-[#101012] p-4" data-id="element-93">
      <div className="w-full max-w-2xl flex flex-col h-[90vh] max-h-[700px] bg-[#1A1A1D] text-[#F5F5F7] rounded-2xl shadow-2xl" data-id="element-94">
        <div className="mb-4 p-4 sm:p-6 pb-4 border-b border-gray-700 flex-shrink-0" data-id="element-95">
          <h2 className="text-xl sm:text-2xl font-bold text-center" data-id="element-96">
            Personalize Your Experience
          </h2>
          <p className="text-gray-400 text-center text-sm" data-id="element-97">
            Tell us your preferences to get better recommendations.
          </p>
        </div>

        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6" data-id="element-98">
          {state.messages.map(message => <div key={message.id} className={`flex items-end gap-3 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-99">
              {message.sender === 'system' && <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" data-id="element-100">
                  L
                </div>}
              <div className={`rounded-xl p-3 sm:p-4 max-w-[85%] shadow-md ${message.sender === 'user' ? 'bg-[#007BFF] text-white rounded-br-none' : 'bg-[#2C2F33] rounded-bl-none'}`} data-id="element-101">
                {message.content}
                {message.id === 'intro' && <Lock className="inline ml-1 mb-0.5" size={14} data-id="element-102" />}
              </div>
            </div>)}

          {state.isTyping && <div className="flex items-end gap-3 justify-start" data-id="element-103">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-sky-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" data-id="element-104">
                L
              </div>
              <div className="rounded-xl p-4 bg-[#2C2F33] rounded-bl-none" data-id="element-105">
                <div className="flex space-x-1.5" data-id="element-106">
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-107"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '200ms'
              }} data-id="element-108"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{
                animationDelay: '400ms'
              }} data-id="element-109"></div>
                </div>
              </div>
            </div>}
        </div>

        {!state.isFinished && <div className="mt-auto p-4 sm:p-6 pt-4 border-t border-gray-700 flex-shrink-0" data-id="element-110">
            {currentQuestion && <div className="flex flex-wrap justify-center gap-2 mb-4 animate-fade-in" data-id="element-111">
                {currentQuestion.options.map(option => <button key={option} onClick={() => handleResponse(option)} className="px-3 py-1.5 text-sm bg-[#2C2F33] hover:bg-[#3A3D42] text-[#F5F5F7] rounded-full transition-colors border border-transparent hover:border-[#007BFF]" data-id="element-112">
                    {option}
                  </button>)}
              </div>}

            {currentQuestion?.allowCustom && <div className="animate-fade-in" data-id="element-113">
                <form onSubmit={handleCustomSubmit} className="flex space-x-2" data-id="element-114">
                  <input type="text" value={customInput} onChange={e => setCustomInput(e.target.value)} placeholder="Type your own response..." className="flex-1 bg-[#2C2F33] border border-[#3A3D42] rounded-lg px-4 py-2 text-[#F5F5F7] focus:outline-none focus:ring-2 focus:ring-[#007BFF]" data-id="element-115" />
                  <button type="submit" className="bg-[#007BFF] hover:bg-[#0069D9] text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center" aria-label="Send" data-id="element-116">
                    <Send size={20} data-id="element-117" />
                  </button>
                </form>
              </div>}
          </div>}
      </div>
    </div>;
};
export default PersonalizationChat;